import {useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useId} from '@react-aria/utils';

import {useAppDispatch, useAppShallowEqualSelector} from 'stores/redux_store';

import {ModalDialog, ModalSize} from '@time-webkit/all/organisms/modal-dialog';
import {closeModal} from 'actions/views/modals';
import {ModalIdentifiers} from 'utils/constants';

import {sendToStatist} from '@time-webkit/statist';

import {type Channel} from '@mattermost/types/channels';
import {type TeamInvitationRole} from '@mattermost/types/teams';

import {InviteMembersForm} from './invite_members_form';
import {LinkInviteMember} from './link-invite-member';
import {InviteMembersSummary} from './invite_members_summary';
import {inviteMembersContext} from './invite_members_context';
import {getInviteMembersData} from './selectors/get_invite_members_data';
import {CHANNELS_LIMIT_DEFAULT} from './consts';

type Props = {
    role?: TeamInvitationRole;
    initialChannels?: Channel[];
}

const DEFAULT_ROLE: TeamInvitationRole = 'team_user';
const DEFAULT_INITIAL_CHANNELS: Channel[] = [];

export const InviteMembersModal = ({role = DEFAULT_ROLE, initialChannels = DEFAULT_INITIAL_CHANNELS}: Props) => {
    const intl = useIntl();
    const inviteMemberModalId = useId();
    const inviteSummaryModalId = useId();
    const {
        allowedGuestDomains,
        allowedTeamDomains,
        allowedCurrentTeamDomains,
        archivedChannelsMap,
        canAddMembers,
        canInviteGuests,
        canInviteGuestsPlus,
        channels,
        config,
        currentTeam,
        license,
    } = useAppShallowEqualSelector(getInviteMembersData);
    const {success, sendingIssues, rateLimitIssues, result, reset} = useContext(inviteMembersContext);
    const emailsLimit = Number(config.EmailRateLimitMaxBurst) || undefined;
    const guestChannelsLimit = Number(license.ChannelCountOfRestrictedGuest);
    const guestPlusChannelsLimit = Number(config.GuestInviteChannelsCount || CHANNELS_LIMIT_DEFAULT);

    const dispatch = useAppDispatch();

    const successModalTitle = useMemo(() => {
        let invitationsSent = result.length;
        if (sendingIssues.length > 0) {
            invitationsSent -= sendingIssues.length;
        } else if (rateLimitIssues.length > 0) {
            invitationsSent -= rateLimitIssues.length;
        }
        if (invitationsSent < result.length) {
            return intl.formatMessage({
                id: 'invite_members.invites_have_been_sent_with_issues',
                defaultMessage: 'Invited {count} out of {total} {total, plural, one {user} other {users}} to the {team}',
            }, {
                total: result.length,
                count: invitationsSent,
                team: currentTeam?.display_name,
            });
        }
        return intl.formatMessage({
            id: 'invite.members.invites_have_been_sent',
            defaultMessage: 'Invited to the {team}',
        }, {team: currentTeam?.display_name});
    }, [sendingIssues.length, rateLimitIssues.length, intl, result.length, currentTeam?.display_name]);

    const handleClose = () => dispatch(closeModal(ModalIdentifiers.INVITE_MEMBERS));

    const handleInviteMoreButtonClick = () => {
        sendToStatist('team.invite.sendAgain', {
            teamid: currentTeam!.id,
        });
        reset();
    };

    if (!currentTeam) {
        return null;
    }

    const renderInviteMemberLink = () => {
        if (config.EnableTeamInvitations === 'true') {
            return <LinkInviteMember containerId={inviteMemberModalId} teamId={currentTeam.id}/>;
        }

        return null;
    };

    if (!success) {
        return (
            <ModalDialog
                id={inviteMemberModalId}
                isOpen={true}
                size={ModalSize.MEDIUM}
                title={intl.formatMessage(
                    {
                        id: 'invite.members.invite_members',
                        defaultMessage: 'Invite to the {team}',
                    },
                    {
                        team: currentTeam.display_name,
                    },
                )}
                onClose={handleClose}
            >
                <InviteMembersForm
                    archivedChannelsMap={archivedChannelsMap}
                    containerId={inviteMemberModalId}
                    canAddMembers={canAddMembers}
                    canInviteGuests={canInviteGuests}
                    canInviteGuestsPlus={canInviteGuestsPlus}
                    guestChannelsLimit={guestChannelsLimit}
                    guestPlusChannelsLimit={guestPlusChannelsLimit}
                    team={currentTeam}
                    role={role}
                    channels={initialChannels}
                    autocompleteChannels={channels}
                    allowedCurrentTeamDomains={allowedCurrentTeamDomains}
                    allowedGuestDomains={allowedGuestDomains}
                    allowedTeamDomains={allowedTeamDomains}
                    emailsLimit={emailsLimit}
                    onCancel={handleClose}
                    renderInviteMemberLink={renderInviteMemberLink}
                />
            </ModalDialog>
        );
    }

    return (
        <ModalDialog
            id={inviteSummaryModalId}
            isOpen={true}
            size={ModalSize.MEDIUM}
            title={successModalTitle}
            confirmText={intl.formatMessage({
                id: 'invite.members.ok',
                defaultMessage: 'Ok',
            })}
            cancelText={intl.formatMessage({
                id: 'invite.members.invite_more',
                defaultMessage: 'Invite more',
            })}
            onCancel={handleInviteMoreButtonClick}
            onConfirm={handleClose}
            onClose={handleClose}
            data-test-id='ResultsInvites'
        >
            <InviteMembersSummary containerId={inviteSummaryModalId} />
        </ModalDialog>
    );
};
